import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismInput } from '@mylighthouse/prism-ember';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';

interface DateRangePickerSignature {
  Args: {
    endDate?: Dayjs;
    isValid?: boolean;
    onEndDateChange: (date: Dayjs | null) => void;
    onStartDateChange: (date: Dayjs | null) => void;
    startDate?: Dayjs;
  };
}

export default class DateRangePicker extends Component<DateRangePickerSignature> {
  @service declare intl: Services['intl'];

  @tracked endDateString = '';
  @tracked hasEndDateError = false;
  @tracked hasStartDateError = false;
  @tracked startDateString = '';

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get endDateErrorMessage() {
    if (!this.hasEndDateError) {
      return '';
    }

    if (!this.endDateString) {
      return this.intl.t('payments.date_range_picker.end_date_required');
    }

    if (this.startDateString && this.endDateString) {
      const startDate = dayjs(this.startDateString);
      const endDate = dayjs(this.endDateString);

      if (endDate.isBefore(startDate)) {
        return this.intl.t(
          'payments.date_range_picker.end_date_must_be_after_start',
        );
      }
    }

    if (dayjs(this.endDateString).isAfter(dayjs())) {
      return this.intl.t(
        'payments.date_range_picker.end_date_cannot_be_future',
      );
    }

    if (dayjs(this.endDateString).isBefore(dayjs().subtract(5, 'year'))) {
      return this.intl.t(
        'payments.date_range_picker.end_date_cannot_be_more_than_5_years_ago',
      );
    }

    return this.intl.t('payments.date_range_picker.invalid_end_date');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get initialEndDate() {
    if (this.args.endDate) {
      return this.args.endDate.format('YYYY-MM-DD');
    }
    return '';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get initialStartDate() {
    if (this.args.startDate) {
      return this.args.startDate.format('YYYY-MM-DD');
    }
    return '';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isValid() {
    return (
      !this.hasStartDateError &&
      !this.hasEndDateError &&
      this.startDateString &&
      this.endDateString
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get maxEndDate() {
    return dayjs().format('YYYY-MM-DD');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get maxStartDate() {
    if (this.endDateString) {
      return this.endDateString;
    }
    return dayjs().format('YYYY-MM-DD');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get minEndDate() {
    if (this.startDateString) {
      return this.startDateString;
    }
    return dayjs().subtract(5, 'year').format('YYYY-MM-DD');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get minStartDate() {
    return dayjs().subtract(5, 'year').format('YYYY-MM-DD');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get startDateErrorMessage() {
    if (!this.hasStartDateError) {
      return '';
    }

    if (!this.startDateString) {
      return this.intl.t('payments.date_range_picker.start_date_required');
    }

    if (dayjs(this.startDateString).isAfter(dayjs())) {
      return this.intl.t(
        'payments.date_range_picker.start_date_cannot_be_future',
      );
    }

    if (dayjs(this.startDateString).isBefore(dayjs().subtract(5, 'year'))) {
      return this.intl.t(
        'payments.date_range_picker.start_date_cannot_be_more_than_5_years_ago',
      );
    }

    return this.intl.t('payments.date_range_picker.invalid_start_date');
  }

  validateEndDate(value: string): void {
    this.hasEndDateError = false;

    if (!value) {
      this.hasEndDateError = true;
      return;
    }

    const endDate = dayjs(value);

    if (!endDate.isValid()) {
      this.hasEndDateError = true;
      return;
    }

    if (endDate.isAfter(dayjs())) {
      this.hasEndDateError = true;
      return;
    }

    if (endDate.isBefore(dayjs().subtract(5, 'year'))) {
      this.hasEndDateError = true;
      return;
    }

    if (this.startDateString) {
      const startDate = dayjs(this.startDateString);
      if (endDate.isBefore(startDate)) {
        this.hasEndDateError = true;
        return;
      }
    }
  }

  validateStartDate(value: string): void {
    this.hasStartDateError = false;

    if (!value) {
      this.hasStartDateError = true;
      return;
    }

    const startDate = dayjs(value);

    if (!startDate.isValid()) {
      this.hasStartDateError = true;
      return;
    }

    if (startDate.isAfter(dayjs())) {
      this.hasStartDateError = true;
      return;
    }

    if (startDate.isBefore(dayjs().subtract(5, 'year'))) {
      this.hasStartDateError = true;
      return;
    }
  }

  @action
  onEndDateChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    this.endDateString = value;

    this.validateEndDate(value);

    const date = value && !this.hasEndDateError ? dayjs(value) : null;
    this.args.onEndDateChange(date);
  }

  @action
  onStartDateChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    this.startDateString = value;

    this.validateStartDate(value);

    const date = value && !this.hasStartDateError ? dayjs(value) : null;
    this.args.onStartDateChange(date);

    if (this.endDateString) {
      this.validateEndDate(this.endDateString);
    }
  }

  <template>
    <div class="flex gap-400">
      <div class="flex w-[150px]">
        <PrismInput
          @errorMessage={{this.startDateErrorMessage}}
          @hasErrors={{this.hasStartDateError}}
          @label={{t "payments.from"}}
          @onInput={{this.onStartDateChange}}
          @value={{or this.startDateString this.initialStartDate}}
          max={{this.maxStartDate}}
          min={{this.minStartDate}}
          type="date"
        />
      </div>

      <div class="flex w-[150px]">
        <PrismInput
          @errorMessage={{this.endDateErrorMessage}}
          @hasErrors={{this.hasEndDateError}}
          @label={{t "payments.to"}}
          @onInput={{this.onEndDateChange}}
          @value={{or this.endDateString this.initialEndDate}}
          max={{this.maxEndDate}}
          min={{this.minEndDate}}
          type="date"
        />
      </div>
    </div>
  </template>
}
